import React, { useState, useEffect } from "react";
import GridLayout from "react-grid-layout";
import AIAlerts from "./components/AIAlerts";
import MissionPerformance from "./components/MissionPerformance";
import ScheduledActivities from "./components/ScheduledActivities";
import TotalRobots from "./components/TotalRobots";
import ScheduledTaskLists from "./components/ScheduledTaskLists";
import "react-grid-layout/css/styles.css";
// import "react-resizable/css/styles.css";
import { Container, Button, Typography, Box, Card } from "@mui/material";

const Dashboard = () => {
  const [isLocked, setIsLocked] = useState(() => {
    const savedLockState = localStorage.getItem("isLocked");
    return savedLockState === "true";
  });

  const [layout, setLayout] = useState(() => {
    const savedLayout = JSON.parse(localStorage.getItem("gridLayout"));
    return (
      savedLayout || [
        { i: "ai-alerts", x: 0, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
        { i: "mission-performance", x: 4, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
        { i: "scheduled-activities", x: 0, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
        { i: "total-robots", x: 4, y: 2, w: 4, h: 2, minW: 2, minH: 2 },
        { i: "scheduled-tasklists", x: 0, y: 4, w: 8, h: 3, minW: 2, minH: 3 },
      ]
    );
  });

  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  useEffect(() => {
    const updateContainerWidth = () => {
      setContainerWidth(window.innerWidth);
    };

    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  const handleLayoutChange = (newLayout) => {
    if (!isLocked) {
      setLayout(newLayout);
      localStorage.setItem("gridLayout", JSON.stringify(newLayout));
    }
  };

  const toggleLock = () => {
    const newLockState = !isLocked;
    setIsLocked(newLockState);
    localStorage.setItem("isLocked", newLockState.toString());
  };

  const getCols = () => {
    if (containerWidth < 600) return 1; // Mobile
    if (containerWidth < 960) return 2; // Tablet
    if (containerWidth < 1200) return 4; // Small laptop
    return 8; // Desktop
  };

  return (
    <Container maxWidth="lg" sx={{ margin: "0 auto", padding: 2 }}>
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 2, sm: 0 } }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          ROBOT DASHBOARD
        </Typography>
        <Button
          variant="contained"
          color={isLocked ? "secondary" : "primary"}
          onClick={toggleLock}
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            padding: "8px 16px",
          }}
        >
          {isLocked ? "Unlock Grid" : "Lock Grid"}
        </Button>
      </Box>

      {/* Responsive Grid Layout */}
      <GridLayout
        className="layout"
        layout={layout}
        cols={getCols()}
        rowHeight={120}
        width={containerWidth - 32} // Adjust for container padding
        margin={[16, 16]}
        isDraggable={!isLocked}
        isResizable={!isLocked}
        compactType="vertical"
        preventCollision={true}
        onLayoutChange={handleLayoutChange}
      >
        <Card
          key="ai-alerts"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            padding: { xs: 1, sm: 2 },
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)" },
            height: "100%",
          }}
        >
          <AIAlerts />
        </Card>
        <Card
          key="mission-performance"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            padding: { xs: 1, sm: 2 },
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)" },
            height: "100%",
          }}
        >
          <MissionPerformance />
        </Card>
        <Card
          key="scheduled-activities"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            padding: { xs: 1, sm: 2 },
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)" },
            height: "100%",
          }}
        >
          <ScheduledActivities />
        </Card>
        <Card
          key="total-robots"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            padding: { xs: 1, sm: 2 },
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)" },
            height: "100%",
          }}
        >
          <TotalRobots />
        </Card>
        <Card
          key="scheduled-tasklists"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            padding: { xs: 1, sm: 2 },
            transition: "box-shadow 0.3s ease",
            "&:hover": { boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)" },
            height: "100%",
          }}
        >
          <ScheduledTaskLists />
        </Card>
      </GridLayout>
    </Container>
  );
};

export default Dashboard;
